import { PrismaEnums, WithAuthRole, isFullAirAdmin } from '@airreading/common';

export const allRoles = Object.values(PrismaEnums.Role);
export const RolesLabelMap: Record<PrismaEnums.Role, string> = {
  [PrismaEnums.Role.student]: 'Student',
  [PrismaEnums.Role.parent]: 'Parent',
  [PrismaEnums.Role.coach]: 'Teacher',
  [PrismaEnums.Role.org_admin]: 'District Admin',
  [PrismaEnums.Role.air_admin]: 'Air Admin',
  [PrismaEnums.Role.air_limited_admin]: 'Air Limited Admin',
  [PrismaEnums.Role.school_admin]: 'School Admin',
  [PrismaEnums.Role.school_contact]: 'School Contact',
  [PrismaEnums.Role.edge_coach]: 'Edge Tutors',
};

export type AirAdminOrLimitedAdminRole = typeof PrismaEnums.Role.air_admin | typeof PrismaEnums.Role.air_limited_admin;

/**
 * Which of the two admin roles the user actually holds, or null if they hold
 * neither. Returns `air_admin` in the (invalid) case where both are held.
 * Unlike `isAirAdmin`, this distinguishes the two roles. Use it whenever
 * escalation of roles or privileges is relevant.
 */
export function heldAirAdminOrLimitedAdminRole(user: WithAuthRole): AirAdminOrLimitedAdminRole | null {
  const userRoles = 'user_roles' in user ? user.user_roles : user.userRoles;
  if (userRoles.some(({ role }) => role === PrismaEnums.Role.air_admin)) {
    return PrismaEnums.Role.air_admin;
  }
  if (userRoles.some(({ role }) => role === PrismaEnums.Role.air_limited_admin)) {
    return PrismaEnums.Role.air_limited_admin;
  }
  return null;
}

/**
 * The admin roles `actingUser` may manage on `user`: the one role the user
 * holds (a revoke), or either role when they hold neither (a promote each).
 * Empty for anyone who is not a full `air_admin`.
 */
export function manageableAirAdminRoles(user: WithAuthRole, actingUser: WithAuthRole): AirAdminOrLimitedAdminRole[] {
  if (!isFullAirAdmin(actingUser)) {
    return [];
  }

  const heldRole = heldAirAdminOrLimitedAdminRole(user);
  return heldRole ? [heldRole] : [PrismaEnums.Role.air_admin, PrismaEnums.Role.air_limited_admin];
}

/**
 * Whether `actingUser` may create users (new accounts, not profiles or roles
 * added to an existing user). Only a full `air_admin` may: an
 * `air_limited_admin` sees PII masked, so they could not read back the account
 * they just created. Enforced server-side in
 * `apps/server/src/auth/user-creation.ts`.
 */
export function canCreateUsers(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser);
}

/**
 * Whether `actingUser` may purge a client's data. Only a full `air_admin` may:
 * purging is irreversible, and an `air_limited_admin` sees PII masked, so they
 * could not confirm what they were about to destroy. Enforced server-side in
 * `assertCanPurgeData`.
 */
export function canPurgeData(actingUser: WithAuthRole): boolean {
  return isFullAirAdmin(actingUser);
}

/**
 * Label for the menu item and dialog managing `role`, e.g. "Revoke Air Limited
 * Admin" or "Promote as Air Limited Admin"
 */
export function manageAirAdminLabel(user: WithAuthRole, role: AirAdminOrLimitedAdminRole): string {
  const isHeld = heldAirAdminOrLimitedAdminRole(user) === role;
  return `${isHeld ? 'Revoke' : 'Promote as'} ${RolesLabelMap[role]}`;
}
